"use client"

import { type ComponentProps } from "react"

import { FiltersPanel as AbstractFiltersPanel } from "@unlikelystudio/react-ecommerce-hooks"

import type { ExplicitAny, PropsWithClassName } from "~/@types/generics"
import { SORT_BY_PARAM } from "~/lib/shopify/constants/collection-filters"
import type { ShopifySearchFiltersData } from "~/hooks/useShopifyCollectionProducts"
import { FilterFacet } from "~/components/ui/Collection/CollectionDefault/components/PanelFilters/FilterFacet/index.client"
import InlineCta from "~/components/ui/InlineCta"
import RoundedCta from "~/components/ui/RoundedCta"
import { FORM_NAMES, FormTemplate } from "~/components/forms/FormTemplate"
import { DrawerHeader } from "~/components/shared/Drawer/Header/index.client"
import { Drawer, DrawerContent } from "~/components/shared/Drawer/index.client"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"
import { useSearchAndDiscoveryContext } from "~/providers/SearchAndDiscoveryProvider"
import { objectEntries } from "~/utils/object-entries"
import { objectValues } from "~/utils/object-values"

import { sprinkles } from "~/styles/sprinkles.css"

import * as css from "./style.css"

type PanelFiltersProps = PropsWithClassName<
  Required<Pick<ComponentProps<typeof Drawer>, "open" | "onOpenChange">> & Pick<ShopifySearchFiltersData, "facets">
>

export function PanelFilters({ facets: currentFacets, open, onOpenChange }: PanelFiltersProps) {
  const t = useTranslate()
  const { filters, filtersCount, setFilters, setPage, reset } = useSearchAndDiscoveryContext()

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <div className={css.wrapper}>
          <DrawerHeader
            isSticky
            className={css.top}
            title={`${t("filters_title")}${filtersCount > 0 ? ` (${filtersCount})` : ``}`}
          >
            {filtersCount > 0 && (
              <InlineCta customLineActive={true} onClick={reset} className={sprinkles({ marginLeft: 10 })}>
                {t("filters_reset")}
              </InlineCta>
            )}
          </DrawerHeader>
          <div className={css.content}>
            <FormTemplate
              className={css.formFilters}
              formName={FORM_NAMES.FILTERS}
              components={{
                button: (props) => {
                  switch (props?.type) {
                    case "button":
                      return null as unknown as JSX.Element
                    default:
                      return null as unknown as JSX.Element
                  }
                },
              }}
            >
              <AbstractFiltersPanel
                defaultValues={filters}
                facetComponent={({ name }) => {
                  return <FilterFacet name={name} facets={currentFacets} />
                }}
                facets={
                  currentFacets?.map((facet, index) => ({
                    name: facet?.processedName,
                    index,
                  })) ?? []
                }
                updateMethod="onChange"
                onSubmit={() => {}}
                onChange={(data) => {
                  const selectedFilters = objectEntries(data)?.reduce((acc, [key, values]) => {
                    if (!values || key === SORT_BY_PARAM) {
                      return acc
                    }

                    if (!Array.isArray(values)) {
                      return {
                        ...acc,
                        [key]: objectValues(values as Record<string, ExplicitAny>)?.filter(Boolean),
                      }
                    }

                    return {
                      ...acc,
                      [key]: values?.filter(Boolean) ?? [],
                    }
                  }, {} as typeof filters)

                  const hasDifferentFilters = objectEntries(selectedFilters)?.some(([key, values]) => {
                    if (!filters?.[key]?.length && !values?.length) return false
                    const hasAddedValues = values?.some((value) => !filters?.[key]?.includes(value))
                    const hasRemovedValues = filters?.[key]?.some((value) => !values?.includes(value))

                    return hasAddedValues || hasRemovedValues
                  })

                  if (hasDifferentFilters) {
                    setFilters(selectedFilters)
                    setPage(1)
                    scrollToTop()
                  }
                }}
              />
            </FormTemplate>
          </div>
          <div className={css.bottom}>
            <RoundedCta
              size="large"
              width="full"
              theme="blue-deep"
              type="primary"
              onClick={() => {
                onOpenChange(false)
              }}
            >
              {t("filters_apply")}
            </RoundedCta>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
